/**
 * The Layerit mark: a near-black rounded tile with the letter knocked out of it.
 *
 * This is the *same* mark as `apps/website/src/components/BrandMark.astro` and
 * `apps/website/public/favicon.svg` — the same 32-unit viewBox, the same 7-unit corner, the
 * same 2.6-unit stroke — because the website, the console and the browser tab are three views
 * of one product, and a second drawing of the mark is the classic way those three drift.
 *
 * ## Why the colours are custom properties rather than utilities
 *
 * The tile and its cut are bound to `--brand-tile` / `--brand-cut`, falling back to the
 * primary-button pair. That is what lets the mark sit on the near-white console ground, on the
 * dark claim panel, in the sidebar and in a favicon without a single per-use override: each of
 * those contexts re-binds two tokens, exactly as the rest of the system does.
 *
 * `aria-hidden` is not decoration. The mark never carries meaning on its own here — the
 * wordmark or the heading beside it does — and announcing "L" before "Layerit" is noise.
 */
interface BrandMarkProps {
	/** Rendered edge length in px. 28 in the sidebar, 40 on the loading screen. */
	size?: number
	className?: string
}

export function BrandMark({ size = 32, className }: BrandMarkProps) {
	return (
		<svg className={className} width={size} height={size} viewBox="0 0 32 32" role="presentation" aria-hidden="true" focusable="false">
			<rect x="0" y="0" width="32" height="32" rx="7" fill="var(--brand-tile, var(--cta))" />
			<path
				d="M11.5 9v14h9"
				fill="none"
				stroke="var(--brand-cut, var(--cta-ink))"
				strokeWidth="2.6"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	)
}
